import { valibotResolver } from '@hookform/resolvers/valibot'
import { Checkbox, Input } from 'antd'
import { isSameDay, parseISO, startOfDay } from 'date-fns'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  type Control,
  Controller,
  type FieldError,
  type FieldErrors,
  FormProvider,
  type Resolver,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import { Button, ModalBody, ModalFooter } from '@/components/elements'
import { REACT_QUERY_KEYS } from '@/constants'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { PackageCountFormatValue } from '@/features/shipment-order-details/components'
import { invalidateShipmentOrders } from '@/features/vehicle-dispatch-boards'
import { highlightedShipmentOrderIdAtom } from '@/features/vehicle-dispatch-boards/states'
import {
  type AutoCalculateIncomeEntity,
  type CreateShipmentOrderDto,
  type ShipmentOrdersListExEntity,
  type UserMeExEntity
} from '@/libs/api'
import { useNumericValidation } from '@/libs/hooks'
import { queryClient } from '@/libs/react-query/query-client'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import {
  createShipmentOrder,
  updateNotDispatchedShipmentOrder,
  type UpdateShipmentOrderHistoryRequest
} from '../../api'
import {
  useRegularDatesBaseSync,
  useRegularGroupOrders,
  useShipmentOrderHistoriesSelectQuery,
  useShipmentOrderHistoryDetailQuery
} from '../../hooks'
import {
  createDefaultShipmentOrderFormValue,
  type CreateShipmentOrderFormValue,
  mapShipmentOrderAdditionalFormValueToDto,
  mapToUpdateNotDispatchedShipmentOrderFormValue,
  mapWaypointFormValuesToDto,
  refinedCreateShipmentOrderFormValueSchema,
  type UpdateNotDispatchedShipmentOrderFormDto,
  updateNotDispatchedShipmentOrderFromValueSchema
} from '../../models'
import { shipmentOrderHistoriesQueryKeys } from '../../query-keys'
import {
  RegularScheduleCalendarPanel,
  RegularScheduleToggleButton
} from '../RegularScheduleFooterSection'
import { ShipmentOrderFormCard } from '../ShipmentOrderFormCard'
import { ShipmentOrderHistoryUpdateModal } from '../ShipmentOrderHistoryModal'

import { type ShipmentOrderFormMode } from './shipment-order-form-mode'

/**
 * Helper function to extract error messages including root errors
 * @param errors react-hook-formのerrorsオブジェクト
 * @returns 最初に見つかったエラーメッセージ、またはnull
 */
const getFirstErrorMessage = (
  errors: FieldErrors<UpdateNotDispatchedShipmentOrderFormDto>
): string | null => {
  // 最初にrootエラーをチェック
  if (errors.root?.message) {
    return errors.root.message
  }
  // 次にフィールド固有のエラーをチェック
  for (const field in errors) {
    // フィールドエラーの型を明示的に指定
    const fieldError = errors[field as keyof typeof errors] as
      | FieldError
      | undefined
    if (fieldError?.message) {
      return fieldError.message
    }
  }
  return null
}

/** 時間制限フィールドを ISO 文字列へ変換する（未入力は null のまま） */
const toNullableISOString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value

  const isDate = Object.prototype.toString.call(value) === '[object Date]'
  if (isDate && 'toISOString' in (value as Date)) {
    return (value as Date).toISOString()
  }
  if (typeof value === 'object' && value !== null && 'toISOString' in value) {
    try {
      // @ts-expect-error - 動的なメソッド呼び出し
      return value.toISOString()
    } catch {
      return null
    }
  }
  return null
}

type CommonProps = {
  authUser: UserMeExEntity
  tenantKey: string
  onClose: () => void
  setIsCancelDisabled: (v: boolean) => void
}

type Props = CommonProps &
  (
    | {
        mode: Extract<ShipmentOrderFormMode, 'notDispatchedUpdate'>
        shipmentOrder: ShipmentOrdersListExEntity
        onSuccess: () => void | Promise<void>
      }
    | {
        mode: Extract<ShipmentOrderFormMode, 'create'>
        shippingDate: string
        onCreated?: () => void
      }
  )

/**
 * 配送依頼フォームのコンテンツ。
 * `mode` により「未配車の配送依頼編集 (notDispatchedUpdate)」と
 * 「新規作成 (create)」を出し分ける。ラッパーは編集が `ShipmentOrderFormModal`
 * (配車板) / `ShipmentOrderEditModal` (一覧)、作成が `ShipmentOrderCreateModal`。
 */
export const ShipmentOrderFormModalContent: FC<Props> = (props) => {
  const { authUser, tenantKey, onClose, setIsCancelDisabled, mode } = props
  const shipmentOrder =
    props.mode === 'notDispatchedUpdate' ? props.shipmentOrder : undefined
  const onUpdateSuccess =
    props.mode === 'notDispatchedUpdate' ? props.onSuccess : undefined
  const createShippingDate =
    props.mode === 'create' ? props.shippingDate : undefined
  const onCreated = props.mode === 'create' ? props.onCreated : undefined

  if (mode === 'notDispatchedUpdate' && !shipmentOrder) {
    throw new Error('shipmentOrder is required')
  }
  if (mode === 'create' && !createShippingDate) {
    throw new Error('shippingDate is required')
  }

  const selectedDcId = useAtomValue(selectedDcIdAtom)

  // 定期運行グループの配送依頼を取得（notDispatchedUpdate 専用。
  // create は対象依頼が無いため呼ばない）
  const { data: regularGroupData } = useRegularGroupOrders({
    tenantKey,
    regularGroupId: shipmentOrder?.regularGroupId ?? null,
    enabled: mode === 'notDispatchedUpdate' && !!shipmentOrder?.regularGroupId
  })

  const regularGroupOrders = useMemo(
    () => regularGroupData?.data ?? [],
    [regularGroupData]
  )

  // インポート依頼の「編集を許可」状態。カードから通知を受けて定期運行の可否に使う
  const [allowFullEditForImported, setAllowFullEditForImported] =
    useState(false)

  // カレンダー表示状態（create は既存の定期運行が無いため常に false から開始）
  const [showCalendar, setShowCalendar] = useState(
    mode === 'notDispatchedUpdate' ? !!shipmentOrder?.regularGroupId : false
  )

  // 定期運行の日付リスト（create は空から開始）
  const [regularDates, setRegularDates] = useState<Date[]>(() => {
    if (mode === 'notDispatchedUpdate' && shipmentOrder) {
      return [startOfDay(new Date(shipmentOrder.group.shippingDate))]
    }
    return []
  })

  // 既存の配送依頼の日付マップ (日付 -> 配送依頼)。create は regularGroupOrders が
  // 常に空のため自然に空になる。
  const existingDatesMap = useMemo(() => {
    const map = new Map<string, ShipmentOrdersListExEntity>()
    regularGroupOrders.forEach((order) => {
      const dateKey = startOfDay(
        new Date(order.group.shippingDate)
      ).toISOString()
      map.set(dateKey, order)
    })
    return map
  }, [regularGroupOrders])

  // 実績化されている日付のSet
  const tourDatesSet = useMemo(() => {
    const set = new Set<string>()
    regularGroupOrders.forEach((order) => {
      if (order.hasTour) {
        const dateKey = startOfDay(
          new Date(order.group.shippingDate)
        ).toISOString()
        set.add(dateKey)
      }
    })
    return set
  }, [regularGroupOrders])

  // 初期値。create は shippingDate prop を発日/着日の初期値に、
  // notDispatchedUpdate は既存の配送依頼を初期値にマッピングする。
  const initialFormValues = useMemo(() => {
    if (mode === 'create') {
      return {
        ...createDefaultShipmentOrderFormValue,
        shippingDate: createShippingDate ? parseISO(createShippingDate) : null,
        deliveryDate: createShippingDate ? parseISO(createShippingDate) : null
      } as unknown as UpdateNotDispatchedShipmentOrderFormDto
    }
    const mapped = mapToUpdateNotDispatchedShipmentOrderFormValue({
      shipmentOrder: shipmentOrder!
    })
    // #4267: 編集時の税区分・内税・端数処理は DB 値をそのまま表示する（拠点デフォルトで補完しない）。
    // 補完すると「設定済みに見えるのに保存値は null のまま」の乖離が起きるため PdM 確認の上で廃止
    // （旧: VOC_PJ-844 の 既存値 → 拠点デフォルト 補完。#4267 コメント参照）。
    const values = { ...mapped }
    // マニュアル注文で品目が空の場合、空行をデフォルト値に含めておく
    // こうすることで appendItem がマウント時に呼ばれず、watch callback が余分に発火しない
    if (shipmentOrder!.isManual && values.items.length === 0) {
      return {
        ...values,
        items: [
          {
            detailId: -1,
            itemCode: null,
            itemName: '',
            packagedLoadQuantity: null,
            packageCount: null,
            quantity: null,
            weight: null
          }
        ]
      } as unknown as UpdateNotDispatchedShipmentOrderFormDto
    }
    return values as unknown as UpdateNotDispatchedShipmentOrderFormDto
  }, [mode, shipmentOrder, createShippingDate])

  // バリデーションスキーマ。mode に応じて出し分ける。
  const resolver = useMemo(
    () =>
      (mode === 'create'
        ? valibotResolver(refinedCreateShipmentOrderFormValueSchema)
        : valibotResolver(
            updateNotDispatchedShipmentOrderFromValueSchema
          )) as unknown as Resolver<UpdateNotDispatchedShipmentOrderFormDto>,
    [mode]
  )

  const formMethods = useForm<UpdateNotDispatchedShipmentOrderFormDto>({
    defaultValues: initialFormValues,
    resolver,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const {
    control,
    formState: { isSubmitting, isValid, errors },
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset
  } = formMethods

  useEffect(() => {
    setIsCancelDisabled(isSubmitting)
  }, [isSubmitting, setIsCancelDisabled])

  const [hasBeenDirty, setHasBeenDirty] = useState(false)

  // 保存ボタン活性化：初期値との差分が生じたときのみ dirty にする
  // DatePicker の開閉は値を変えないため誤発火しない
  // (create は dirty ガードを使わないため、この値は notDispatchedUpdate でのみ参照する)
  useEffect(() => {
    const initialSnapshot = JSON.stringify(getValues())
    // eslint-disable-next-line prefer-const
    let sub = watch((values) => {
      if (JSON.stringify(values) !== initialSnapshot) {
        setHasBeenDirty(true)
        sub.unsubscribe()
      }
    })
    return () => sub.unsubscribe()
  }, [watch, getValues])

  // lastEstimatedIncome の初期値。create は見積結果が無いため undefined。
  const [lastEstimatedIncome, setLastEstimatedIncome] = useState<
    AutoCalculateIncomeEntity | undefined
  >(
    mode === 'notDispatchedUpdate' && shipmentOrder?.customerContractId
      ? {
          customerContractId: shipmentOrder.customerContractId,
          customerContractName: shipmentOrder.customerContractName,
          income: shipmentOrder.income
        }
      : undefined
  )

  // create: 保存後もこの内容を残すか（フッターの「この内容を保存後も残す」checkbox）
  const [keepContents, setKeepContents] = useState(true)

  // create: 履歴の編集依頼（ShipmentOrderInputHistorySelect の編集ボタンから発火）
  const [editRequest, setEditRequest] =
    useState<UpdateShipmentOrderHistoryRequest | null>(null)

  const setHighlightedShipmentOrderId = useSetAtom(
    highlightedShipmentOrderIdAtom
  )

  const shippingDateWatchValue = watch('shippingDate')
  const deliveryDateWatchValue = watch('deliveryDate')

  // カレンダーの表示/非表示切り替え（notDispatchedUpdate 専用。日付リストは
  // 既存/削除予定を用いて別途管理するため、開閉のみ行う）
  const toggleCalendar = useCallback(() => {
    setShowCalendar((prev) => !prev)
  }, [])

  // 日付選択時の処理（notDispatchedUpdate 専用）
  const handleDateSelect = useCallback(
    (date: Date) => {
      setHasBeenDirty(true)
      setRegularDates((prev) => {
        const dateKey = startOfDay(date).toISOString()

        // 定期運行の日付をクリックした場合、削除リストから取り除く
        if (existingDatesMap.has(dateKey)) {
          setDatesToDelete((prevDeleteSet) => {
            const next = new Set(prevDeleteSet)
            if (next.has(dateKey)) {
              next.delete(dateKey)
            }
            return next
          })
          return prev
        }

        // 既に選択されていない場合のみ追加
        if (!prev.some((d) => startOfDay(d).toISOString() === dateKey)) {
          return [...prev, startOfDay(date)].sort(
            (a, b) => a.getTime() - b.getTime()
          )
        }
        return prev
      })
    },
    [existingDatesMap]
  )

  // 削除予定の既存日付を管理（notDispatchedUpdate 専用）
  const [datesToDelete, setDatesToDelete] = useState<Set<string>>(new Set())

  // 日付解除時の処理（notDispatchedUpdate 専用）
  const handleDateDeselect = useCallback(
    (date: Date) => {
      const dateKey = startOfDay(date).toISOString()

      // 編集中の配送依頼の日付は解除不可
      const editingDate = startOfDay(
        new Date(shipmentOrder!.group.shippingDate)
      )
      if (startOfDay(date).getTime() === editingDate.getTime()) {
        return
      }

      // 編集中の日付より前の日付は解除不可
      if (startOfDay(date).getTime() < editingDate.getTime()) {
        return
      }

      // 実績化されている日付は解除不可
      if (tourDatesSet.has(dateKey)) {
        return
      }

      setHasBeenDirty(true)

      // 既存の日付の場合は削除予定リストに追加
      if (existingDatesMap.has(dateKey)) {
        setDatesToDelete((prev) => new Set(prev).add(dateKey))
      }

      // 新規追加の日付の場合はregularDatesから削除
      setRegularDates((prev) => {
        const filtered = prev.filter(
          (d) => startOfDay(d).toISOString() !== dateKey
        )

        if (filtered.length === 0) {
          setShowCalendar(false)
        }

        return filtered
      })
    },
    [shipmentOrder, tourDatesSet, existingDatesMap]
  )

  // create: カレンダーを開いている間に発送日が変わったら基準日 1 件へ戻す。
  // 「開いた瞬間」を対象にしないのは、開く操作 (toggleCreateCalendar) が既に
  // 基準日を選択済みにしており、重ねて reset すると effect のタイミング差で
  // 直後のクリックを消してしまうためである (#5232、hook の JSDoc 参照)。
  const resetRegularDatesToBase = useCallback((base: Date) => {
    setRegularDates([base])
  }, [])
  useRegularDatesBaseSync({
    enabled: mode === 'create' && showCalendar,
    shippingDate: shippingDateWatchValue,
    onReset: resetRegularDatesToBase
  })

  // create: カレンダートグル（開くときに発日を選択済みにし、閉じるときは全解除する）
  const toggleCreateCalendar = useCallback(() => {
    setShowCalendar((prev) => {
      const next = !prev
      if (next && shippingDateWatchValue) {
        setRegularDates([startOfDay(new Date(shippingDateWatchValue))])
      } else {
        setRegularDates([])
      }
      return next
    })
  }, [shippingDateWatchValue])

  // create: 日付選択時の処理（既存日付の概念が無いため単純に追加するだけ）
  const handleCreateDateSelect = useCallback((date: Date) => {
    setRegularDates((prev) => {
      if (
        !prev.some(
          (d) => startOfDay(d).getTime() === startOfDay(date).getTime()
        )
      ) {
        return [...prev, startOfDay(date)].sort(
          (a, b) => a.getTime() - b.getTime()
        )
      }
      return prev
    })
  }, [])

  // create: 日付解除時の処理
  const handleCreateDateDeselect = useCallback((date: Date) => {
    setRegularDates((prev) => {
      const filtered = prev.filter(
        (d) => startOfDay(d).getTime() !== startOfDay(date).getTime()
      )
      if (filtered.length === 0) {
        setShowCalendar(false)
      }
      return filtered
    })
  }, [])

  // 時間制限フィールドを文字列に変換する関数
  const toISOString = (value: string): string => {
    if (typeof value === 'string') return value

    // Date型かどうかを安全に確認
    const isDate = Object.prototype.toString.call(value) === '[object Date]'
    if (isDate && 'toISOString' in (value as Date)) {
      return (value as Date).toISOString()
    }

    // 追加のチェック: オブジェクトでtoISOString()メソッドを持つ場合
    if (typeof value === 'object' && value !== null && 'toISOString' in value) {
      try {
        // @ts-expect-error - 動的なメソッド呼び出し
        return value.toISOString()
      } catch (e) {
        return ''
      }
    }
    return ''
  }

  const onSubmit = useCallback(
    async (formValue: UpdateNotDispatchedShipmentOrderFormDto) => {
      const fixedItems = formValue.items.map((item) => {
        let packageCount = item.packageCount
        let packagedLoadQuantity = item.packagedLoadQuantity

        // packageCountが未入力なら自動計算値をセット
        if (
          (packageCount === null || packageCount === undefined) &&
          item.quantity !== null &&
          item.quantity !== 0 &&
          item.packagedLoadQuantity !== null &&
          item.packagedLoadQuantity !== 0
        ) {
          // 数量 / 荷姿単位数量 で計算（小数点第1位で切り上げ）
          packageCount = PackageCountFormatValue(
            item.quantity / item.packagedLoadQuantity
          )
        }

        // packagedLoadQuantityが未入力なら自動計算値をセット
        if (
          (packagedLoadQuantity === null ||
            packagedLoadQuantity === undefined) &&
          item.quantity !== null &&
          item.quantity !== 0 &&
          packageCount !== null &&
          packageCount !== 0
        ) {
          // 数量 / 荷姿数 で計算（小数点第1位で切り上げ）
          packagedLoadQuantity = PackageCountFormatValue(
            item.quantity / packageCount
          )
        }

        return { ...item, packageCount, packagedLoadQuantity }
      })

      try {
        // 確実にデータを含める対策
        const values = getValues()
        const dto =
          values && Object.keys(values).length > 0 ? values : { ...formValue }

        const allRegularDates = new Set<string>()

        // 新規追加の日付を追加
        regularDates.forEach((date) => {
          allRegularDates.add(startOfDay(date).toISOString())
        })

        // 既存の日付で削除予定でないものを追加
        Array.from(existingDatesMap.keys()).forEach((dateKey) => {
          if (!datesToDelete.has(dateKey)) {
            allRegularDates.add(dateKey)
          }
        })

        // 既存の定期運行を編集しているかどうかを判定
        const isEditingRegularSchedule = shipmentOrder!.regularGroupId !== null

        // 削除された日付があるかどうかを判定
        const hasDeletedDates = datesToDelete.size > 0

        // regularDatesを送信する条件:
        // 複数日付がある場合 (新規作成 or 既存編集)
        // 既存の定期運行を編集していて、削除された日付がある場合 (単一日付でも送信)
        const shouldSendRegularDates =
          allRegularDates.size > 1 ||
          (isEditingRegularSchedule && hasDeletedDates)

        // 日付フィールドを正しいフォーマットに変換
        // VOC_PJ-29,431 #1857: 請求側付帯費用のみ form 値 → BE DTO 形に変換して送信。
        // list endpoint は T6 で additionalCharges を include 済のため、初期値は既存行を保持する。
        // 支払側 (払い側) は #3541 指摘-8 で矢羽根単位へ移行したため、配車前 (矢羽根未存在) の
        // 本フォームでは扱わない。
        const additionalChargesDto = (dto.additionalCharges ?? []).map(
          mapShipmentOrderAdditionalFormValueToDto
        )
        // 経由地は未選択行を落として送るため、フォーム値の waypoints は
        // ペイロードから除外し、下で Site.id 配列へ変換して載せ替える。
        const { waypoints: waypointsFormValue, ...dtoWithoutWaypoints } = dto
        const formattedDto = {
          ...dtoWithoutWaypoints,
          shippingDate: dto.shippingDate ? toISOString(dto.shippingDate) : '',
          deliveryDate: dto.deliveryDate ? toISOString(dto.deliveryDate) : '',
          // #3328 VOC_PJ-761 / VOC_PJ-844: 貰い運賃の税区分・内税。
          // defaultValues で拠点(IN_HOUSE)デフォルトを pre-fill しているため、ユーザが
          // 当該項目を編集していない場合は pre-fill 値ではなく元レコード値を送る
          // (未変更で null=カスケード継承 を確定値で上書きしない / Copilot review)。
          incomeTaxCategoryId: formMethods.getFieldState('incomeTaxCategoryId')
            .isDirty
            ? (dto.incomeTaxCategoryId ?? null)
            : (shipmentOrder!.incomeTaxCategoryId ?? null),
          incomeIsTaxInclusive: formMethods.getFieldState(
            'incomeIsTaxInclusive'
          ).isDirty
            ? (dto.incomeIsTaxInclusive ?? null)
            : (shipmentOrder!.incomeIsTaxInclusive ?? null),
          // VOC_PJ-888 #3917: 貰い運賃の端数処理。未編集なら元レコード値を送る（内税と同形）。
          incomeRoundType: formMethods.getFieldState('incomeRoundType').isDirty
            ? (dto.incomeRoundType ?? null)
            : (shipmentOrder!.incomeRoundType ?? null),
          // 編集モーダル: ユーザの手入力値（または既存値）をそのまま送信
          incomeRecordingDate: dto.incomeRecordingDate
            ? toISOString(dto.incomeRecordingDate)
            : undefined,
          items: fixedItems,
          customerContractId: lastEstimatedIncome?.customerContractId ?? null,
          regularDates: shouldSendRegularDates
            ? Array.from(allRegularDates).sort()
            : undefined,
          additionalCharges: additionalChargesDto,
          // VOC_PJ-580: 編集画面からの履歴保存
          saveHistory: dto.saveHistory ?? false,
          historyId: dto.historyId ?? null,
          historyName: dto.historyName ?? null,
          saveMode: dto.saveMode,
          // 経由地: 未選択行(siteId=null)を除外し Site.id と備考を同じ index で送信
          ...mapWaypointFormValuesToDto(waypointsFormValue)
        }

        // バリデーションはhandleSubmitが実施済み
        await updateNotDispatchedShipmentOrder({
          tenantKey,
          orderId: shipmentOrder!.id,
          dto: formattedDto
        })

        await queryClient.invalidateQueries({
          queryKey: [REACT_QUERY_KEYS.VEHICLE_DISPATCH_BOARDS]
        })

        await onUpdateSuccess?.()
        toast.success(SUCCESS_MESSAGES.UPDATED_SHIPMENT_ORDER)
      } catch (error) {
        toastApiErrorMessage(
          error,
          ERROR_MESSAGES.FAILED_TO_UPDATE_SHIPMENT_ORDER
        )
      }
    },
    [
      getValues,
      lastEstimatedIncome?.customerContractId,
      tenantKey,
      onUpdateSuccess,
      regularDates,
      datesToDelete,
      existingDatesMap,
      shipmentOrder,
      formMethods
    ]
  )

  const onCreateSubmit = useCallback(
    async (formValue: UpdateNotDispatchedShipmentOrderFormDto) => {
      const createFormValue =
        formValue as unknown as CreateShipmentOrderFormValue

      // 時間制限フィールドを正規化。品目の荷姿数/荷姿単位数量の未入力分を自動計算値で補完する。
      const normalizedFormValue = {
        ...createFormValue,
        departureTimeRestrictionStart: toNullableISOString(
          createFormValue.departureTimeRestrictionStart
        ),
        departureTimeRestrictionEnd: toNullableISOString(
          createFormValue.departureTimeRestrictionEnd
        ),
        arrivalTimeRestrictionStart: toNullableISOString(
          createFormValue.arrivalTimeRestrictionStart
        ),
        arrivalTimeRestrictionEnd: toNullableISOString(
          createFormValue.arrivalTimeRestrictionEnd
        ),
        items: createFormValue.items?.map((item) => {
          let packageCount = item.packageCount
          let packagedLoadQuantity = item.packagedLoadQuantity

          if (
            (packageCount === null || packageCount === undefined) &&
            item.quantity !== null &&
            item.quantity !== 0 &&
            item.packagedLoadQuantity !== null &&
            item.packagedLoadQuantity !== 0
          ) {
            packageCount = PackageCountFormatValue(
              item.quantity / item.packagedLoadQuantity
            )
          }

          if (
            (packagedLoadQuantity === null ||
              packagedLoadQuantity === undefined) &&
            item.quantity !== null &&
            item.quantity !== 0 &&
            packageCount !== null &&
            packageCount !== 0
          ) {
            packagedLoadQuantity = PackageCountFormatValue(
              item.quantity / packageCount
            )
          }
          return { ...item, packageCount, packagedLoadQuantity }
        }),
        truckModelId: createFormValue.truckModelId ?? null
      }

      // 必須フィールドのチェック（resolver が既に必須化しているため到達しない想定だが、
      // 抜けの受け皿として残す）
      const requiredFields = [
        'customerId',
        'shipmentCategoryId',
        'originSiteId',
        'destSiteId',
        'shippingDate',
        'deliveryDate',
        'incomeRecordingDate'
      ] as const

      const missingFields = requiredFields.filter(
        (field) =>
          normalizedFormValue[field] === undefined ||
          normalizedFormValue[field] === null
      )

      if (missingFields.length > 0) {
        toast.error(
          VALIDATION_MESSAGES.REQUIRED_FIELDS_NOT_ENOUGH(missingFields)
        )
        return
      }

      try {
        // resolver (refinedCreateShipmentOrderFormValueSchema) が保存前に既に検証済みのため、
        // 保存直前の `manualShipmentOrderSchema` による再 parse は行わない（二重検証の削除）。
        // waypoints は未選択行を落として送るため、ペイロードから除外する。
        const {
          waypoints: _waypointsFormValue, // eslint-disable-line @typescript-eslint/no-unused-vars
          ...requestBase
        } = normalizedFormValue

        const apiRequestData = {
          ...requestBase,
          dcId: selectedDcId ?? undefined,
          saveHistory: normalizedFormValue.saveHistory ?? false,
          historyId: normalizedFormValue.historyId,
          historyName: normalizedFormValue.historyName ?? null,
          saveMode: normalizedFormValue.saveMode,
          items: (normalizedFormValue.items ?? []).map((item) => ({
            ...item,
            // 入力値の重量（kg）をそのままセット
            weight: item.weight != null ? item.weight : null
          })),
          truckModelId: normalizedFormValue.truckModelId ?? null,
          // #3463 VOC_PJ-829/851: カスタムキー1〜5
          customKey1: normalizedFormValue.customKey1 ?? null,
          customKey2: normalizedFormValue.customKey2 ?? null,
          customKey3: normalizedFormValue.customKey3 ?? null,
          customKey4: normalizedFormValue.customKey4 ?? null,
          customKey5: normalizedFormValue.customKey5 ?? null,
          // #3328 VOC_PJ-761: 貰い運賃の税区分（案件個別オーバーライド）
          incomeTaxCategoryId: normalizedFormValue.incomeTaxCategoryId ?? null,
          // VOC_PJ-844: 貰い運賃の内税フラグ
          incomeIsTaxInclusive:
            normalizedFormValue.incomeIsTaxInclusive ?? null,
          // VOC_PJ-888 #3917: 貰い運賃の端数処理
          incomeRoundType: normalizedFormValue.incomeRoundType ?? null,
          regularDates:
            regularDates.length > 1
              ? regularDates.map((date) => date.toISOString())
              : undefined,
          customerContractId: lastEstimatedIncome?.customerContractId ?? null,
          // VOC_PJ-29,431 #1857 指摘-4: 請求側付帯費用 (荷主デフォルト含む) を送信
          additionalCharges: (normalizedFormValue.additionalCharges ?? []).map(
            mapShipmentOrderAdditionalFormValueToDto
          ),
          // 経由地: 未選択行(siteId=null)を除外し Site.id と備考を同じ index で送信
          ...mapWaypointFormValuesToDto(normalizedFormValue.waypoints)
        } as unknown as CreateShipmentOrderDto

        const { data } = await createShipmentOrder({
          tenantKey,
          dto: apiRequestData
        })
        // #4570 VOC_PJ-970: 作成された配送依頼を色付き1件にする
        // 定期運行で複数日ぶん作成された場合も、一覧はドロワー表示中の1日ぶんなのでその日の1件を選ぶ
        const createdOrders = Array.isArray(data) ? data : data ? [data] : []
        const createdOrderForDisplayedDate = createdOrders.find((order) =>
          isSameDay(
            parseISO(order.group.shippingDate),
            parseISO(createShippingDate!)
          )
        )
        setHighlightedShipmentOrderId(createdOrderForDisplayedDate?.id ?? null)
      } catch (error) {
        toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_REGISTER)
        return
      }
      toast.success(SUCCESS_MESSAGES.REGISTERED)
      invalidateShipmentOrders({
        tenantKey,
        shippingDate: createShippingDate!
      })
      queryClient.invalidateQueries({
        queryKey: shipmentOrderHistoriesQueryKeys.select(tenantKey)
      })
      onCreated?.()
      if (!keepContents) {
        // 発日・着日は必須項目のため、入力内容を残さない場合でもクリアせず
        // 直前に保存した日付を引き継ぐ（同じ日付で続けて登録できるようにする）。
        reset({
          ...createDefaultShipmentOrderFormValue,
          shippingDate: createFormValue.shippingDate,
          deliveryDate: createFormValue.deliveryDate
        } as unknown as UpdateNotDispatchedShipmentOrderFormDto)
        setRegularDates([])
        setShowCalendar(false)
      } else {
        setValue('historyId', null)
        setValue('saveMode', undefined)
        setValue('historyName', null)
      }
    },
    [
      selectedDcId,
      regularDates,
      lastEstimatedIncome,
      createShippingDate,
      tenantKey,
      reset,
      setValue,
      onCreated,
      setHighlightedShipmentOrderId,
      keepContents
    ]
  )

  const { setNumericRef, validateAllNumericInputs } = useNumericValidation()

  // 既に定期運行に属する配送依頼かどうか。ラベルを「編集」/「作成」で出し分ける
  const hasRegularGroup = !!shipmentOrder?.regularGroupId

  // インポート依頼はカードで「編集を許可」した後のみ定期運行を編集できる
  const canEditRegularSchedule =
    mode === 'create' ||
    (shipmentOrder?.isManual ?? true) ||
    allowFullEditForImported

  const regularScheduleBaseDate = shippingDateWatchValue
    ? new Date(shippingDateWatchValue)
    : null
  const regularScheduleEditingDate =
    mode === 'create'
      ? (regularScheduleBaseDate ?? new Date())
      : new Date(shipmentOrder!.group.shippingDate)

  return (
    <>
      <FormProvider {...formMethods}>
        {/* 定期運行カレンダーを開いてもモーダル全体の高さを変えず、本文のスクロール域を縮める */}
        <form
          className="flex max-h-[70vh] flex-col"
          onSubmit={(e) => {
            if (!validateAllNumericInputs()) {
              e.preventDefault()
              return
            }

            return handleSubmit(
              mode === 'create' ? onCreateSubmit : onSubmit,
              (
                errors: FieldErrors<UpdateNotDispatchedShipmentOrderFormDto>
              ) => {
                const errorMessage = getFirstErrorMessage(errors)

                if (errorMessage) {
                  toast.error(errorMessage)
                } else {
                  toast.error(VALIDATION_MESSAGES.CONFIRM_EACH_ERROR_MESSAGES)
                }
              }
            )(e)
          }}
        >
          <div className="min-h-0 flex-auto overflow-y-auto">
            <ModalBody className="pb-0">
              <ShipmentOrderFormCard
                mode={mode}
                tenantKey={tenantKey}
                authUser={authUser}
                selectedDcId={selectedDcId}
                setNumericRef={setNumericRef}
                sourceOrder={shipmentOrder}
                onAllowFullEditForImportedChange={setAllowFullEditForImported}
                lastEstimatedIncome={lastEstimatedIncome}
                onEstimatedIncomeChange={setLastEstimatedIncome}
                historyLoad={
                  mode === 'create'
                    ? {
                        onEditRequest: (req) =>
                          setEditRequest(
                            req as UpdateShipmentOrderHistoryRequest
                          )
                      }
                    : undefined
                }
              />
            </ModalBody>
          </div>
          <ModalFooter className="shrink-0 flex-col items-stretch gap-1">
            {/* 定期運行カレンダーは履歴チェックボックス行より上に開く（保存ボタンを動かさない） */}
            <RegularScheduleCalendarPanel
              show={showCalendar}
              baseDate={regularScheduleBaseDate}
              selectedDates={regularDates}
              existingDates={
                mode === 'create'
                  ? []
                  : Array.from(existingDatesMap.keys())
                      .filter((key) => !datesToDelete.has(key))
                      .map((key) => new Date(key))
              }
              editingDate={regularScheduleEditingDate}
              tourDates={
                mode === 'create'
                  ? []
                  : Array.from(tourDatesSet).map((key) => new Date(key))
              }
              onDateSelect={
                mode === 'create' ? handleCreateDateSelect : handleDateSelect
              }
              onDateDeselect={
                mode === 'create'
                  ? handleCreateDateDeselect
                  : handleDateDeselect
              }
            />
            {/* VOC_PJ-580: 履歴保存セクション。notDispatchedUpdate/create で分岐の起点
                （履歴の有無の判定元）が異なるため別コンポーネントで持つ */}
            <div className="flex w-full items-start justify-between gap-2">
              <div>
                {mode === 'notDispatchedUpdate' && (
                  <HistorySaveSection
                    control={control}
                    tenantKey={tenantKey}
                    originHistoryId={shipmentOrder?.originHistoryId ?? null}
                  />
                )}
                {mode === 'create' && (
                  <CreateHistorySaveSection
                    control={control}
                    tenantKey={tenantKey}
                  />
                )}
              </div>
              {/* 定期運行の開閉ボタン。履歴チェックボックスと同じ行の右端に置く */}
              <RegularScheduleToggleButton
                showCalendar={showCalendar}
                openLabel={
                  hasRegularGroup
                    ? ACTIONS.EDIT_NOT_DISPATCHED_REGULAR_OPERATION
                    : ACTIONS.CREATE_NOT_DISPATCHED_REGULAR_OPERATION
                }
                disabled={
                  !canEditRegularSchedule ||
                  !shippingDateWatchValue ||
                  !deliveryDateWatchValue
                }
                onToggle={
                  mode === 'create' ? toggleCreateCalendar : toggleCalendar
                }
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              {mode === 'create' && (
                <Checkbox
                  checked={keepContents}
                  onChange={(e) => setKeepContents(e.target.checked)}
                >
                  {ACTIONS.KEEP_CONTENTS_IN_SAVING}
                </Checkbox>
              )}
              <Button onClick={onClose} disabled={isSubmitting}>
                {ACTIONS.CANCEL}
              </Button>
              <Button
                type="primary"
                icon={<FaCheckCircle />}
                htmlType="submit"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  (mode === 'notDispatchedUpdate' && !hasBeenDirty) ||
                  !isValid ||
                  Object.keys(errors).length > 0
                }
              >
                {ACTIONS.SAVE}
              </Button>
            </div>
          </ModalFooter>
        </form>
      </FormProvider>
      {mode === 'create' && (
        <ShipmentOrderHistoryUpdateModal
          tenantKey={tenantKey}
          open={!!editRequest}
          updateRequest={editRequest}
          onSuccess={() => setEditRequest(null)}
          onClose={() => setEditRequest(null)}
        />
      )}
    </>
  )
}

// VOC_PJ-580: 履歴保存セクション（チェックボックス + 分岐A/B）
const HistorySaveSection: FC<{
  control: Control<UpdateNotDispatchedShipmentOrderFormDto>
  tenantKey: string
  originHistoryId: number | null
}> = ({ control, tenantKey, originHistoryId }) => {
  const saveHistory = useWatch({ control, name: 'saveHistory' })
  const saveMode = useWatch({ control, name: 'saveMode' })
  const { setValue } = useFormContext<UpdateNotDispatchedShipmentOrderFormDto>()

  const { data: originHistory, isError: isOriginHistoryError } =
    useShipmentOrderHistoryDetailQuery(tenantKey, originHistoryId, {
      disabled: originHistoryId == null
    })

  // 元の履歴が存在する（削除されていない）場合のみ上書き選択肢を表示する
  const hasValidOriginHistory = originHistoryId != null && !isOriginHistoryError

  // saveHistory が ON になったとき saveMode をデフォルト（overwrite）にリセット
  useEffect(() => {
    if (saveHistory && hasValidOriginHistory) {
      setValue('saveMode', 'overwrite')
    }
  }, [saveHistory]) // eslint-disable-line react-hooks/exhaustive-deps

  const originHistoryLabel = originHistory
    ? originHistory.name ||
      `${originHistory.originSiteName} → ${originHistory.destSiteName}`
    : null

  return (
    <div className="w-full">
      <Controller
        control={control}
        name="saveHistory"
        render={({ field }) => (
          <Checkbox
            checked={field.value ?? false}
            onChange={(e) => field.onChange(e.target.checked)}
          >
            {LABELS.HISTORY_SAVE_CHECKBOX}
          </Checkbox>
        )}
      />
      {saveHistory && hasValidOriginHistory && (
        <div className="ml-6 mt-2">
          <Controller
            control={control}
            name="saveMode"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={field.name}
                    value="overwrite"
                    checked={field.value === 'overwrite' || field.value == null}
                    onChange={() => {
                      field.onChange('overwrite')
                      setValue('historyName', null)
                    }}
                  />
                  <span>
                    {originHistoryLabel
                      ? `${LABELS.HISTORY_OVERWRITE_PREFIX}${originHistoryLabel}${LABELS.HISTORY_OVERWRITE_SUFFIX}`
                      : LABELS.HISTORY_OVERWRITE}
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={field.name}
                    value="new"
                    checked={field.value === 'new'}
                    onChange={() => {
                      field.onChange('new')
                    }}
                  />
                  <span>{LABELS.HISTORY_REGISTER_AS_NEW}</span>
                </label>
              </div>
            )}
          />
          {saveMode === 'new' && (
            <Controller
              control={control}
              name="historyName"
              render={({ field }) => (
                <div className="mt-2">
                  <Input
                    placeholder={LABELS.HISTORY_NAME_PLACEHOLDER}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    className="max-w-xs"
                  />
                </div>
              )}
            />
          )}
        </div>
      )}
      {saveHistory && !hasValidOriginHistory && (
        <div className="ml-6 mt-2">
          <Controller
            control={control}
            name="historyName"
            render={({ field }) => (
              <Input
                placeholder={LABELS.HISTORY_NAME_PLACEHOLDER}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
                className="max-w-xs"
              />
            )}
          />
        </div>
      )}
    </div>
  )
}

// VOC_PJ-580: create モード専用の履歴保存セクション（チェックボックス + 上書き/新規 選択）。
// notDispatchedUpdate 用の HistorySaveSection は「元履歴 (originHistoryId)」の有無で
// 上書き選択肢を出し分けるが、create は「履歴コンボボックスで選択中の履歴 (historyId)」の
// 有無で出し分ける点が異なるため別コンポーネントとして持つ。
const CreateHistorySaveSection: FC<{
  control: Control<UpdateNotDispatchedShipmentOrderFormDto>
  tenantKey: string
}> = ({ control, tenantKey }) => {
  const saveHistory = useWatch({ control, name: 'saveHistory' })
  const saveMode = useWatch({ control, name: 'saveMode' })
  const historyId = useWatch({ control, name: 'historyId' })
  const customerId = useWatch({ control, name: 'customerId' })
  const shipmentCategoryId = useWatch({ control, name: 'shipmentCategoryId' })
  const { setValue } = useFormContext<UpdateNotDispatchedShipmentOrderFormDto>()

  // コンボボックスと同じクエリ（同一キャッシュ）を使う。
  // 削除ミューテーション後にキャッシュが invalidate → リスト再取得 → 削除済みIDはリストから消える。
  // これによりコンボボックスが空になると同時に上書き選択肢も自然に消える。
  const { data: historyList } = useShipmentOrderHistoriesSelectQuery(
    tenantKey,
    {
      customerId: customerId ?? undefined,
      shipmentCategoryId: shipmentCategoryId ?? undefined,
      disabled: historyId == null
    }
  )
  const selectedHistoryItem =
    historyId != null
      ? (historyList?.find((item) => item.id === historyId) ?? null)
      : null

  // 選択中の履歴がリストに存在する（削除されていない）場合のみ上書き選択肢を表示する
  const hasValidHistory = selectedHistoryItem != null

  // saveHistory の ON/OFF・historyId の変化・履歴削除検知に応じて saveMode・historyName を制御する。
  // prevHistoryIdRef で初回マウント時の effect 発火を抑止する。
  // ラジオ切り替え時の historyName 制御は各ラジオの onChange に直接記述する。
  const prevHistoryIdRef = useRef<number | null>(null)
  useEffect(() => {
    const historyIdChanged = prevHistoryIdRef.current !== historyId
    prevHistoryIdRef.current = historyId ?? null

    if (saveHistory && hasValidHistory) {
      setValue('saveMode', 'overwrite')
      setValue('historyName', selectedHistoryItem?.name ?? null)
    } else if (historyIdChanged || !hasValidHistory) {
      setValue('saveMode', undefined)
      setValue('historyName', null)
    }
  }, [saveHistory, historyId, hasValidHistory, selectedHistoryItem, setValue])

  const historyLabel = selectedHistoryItem
    ? selectedHistoryItem.name ||
      `${selectedHistoryItem.originSite} → ${selectedHistoryItem.destSite}`
    : null

  return (
    <div className="w-full">
      <Controller
        control={control}
        name="saveHistory"
        render={({ field }) => (
          <Checkbox
            checked={field.value ?? false}
            onChange={(e) => field.onChange(e.target.checked)}
          >
            {LABELS.HISTORY_SAVE_CHECKBOX}
          </Checkbox>
        )}
      />
      {saveHistory && hasValidHistory && (
        <div className="ml-6 mt-2">
          <Controller
            control={control}
            name="saveMode"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={field.name}
                    value="overwrite"
                    checked={field.value === 'overwrite' || field.value == null}
                    onChange={() => {
                      field.onChange('overwrite')
                      // 上書き時はコンボボックスの表示名をそのまま送信
                      setValue('historyName', selectedHistoryItem?.name ?? null)
                    }}
                  />
                  <span>
                    {historyLabel
                      ? `${LABELS.HISTORY_OVERWRITE_PREFIX}${historyLabel}${LABELS.HISTORY_OVERWRITE_SUFFIX}`
                      : LABELS.HISTORY_OVERWRITE}
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={field.name}
                    value="new"
                    checked={field.value === 'new'}
                    onChange={() => {
                      field.onChange('new')
                      setValue('historyName', null)
                    }}
                  />
                  <span>{LABELS.HISTORY_REGISTER_AS_NEW}</span>
                </label>
              </div>
            )}
          />
          {saveMode === 'new' && (
            <Controller
              control={control}
              name="historyName"
              render={({ field }) => (
                <div className="mt-2">
                  <Input
                    placeholder={LABELS.HISTORY_NAME_PLACEHOLDER}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    className="max-w-xs"
                  />
                </div>
              )}
            />
          )}
        </div>
      )}
      {saveHistory && !hasValidHistory && (
        <div className="ml-6 mt-2">
          <Controller
            control={control}
            name="historyName"
            render={({ field }) => (
              <Input
                placeholder={LABELS.HISTORY_NAME_PLACEHOLDER}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
                className="max-w-xs"
              />
            )}
          />
        </div>
      )}
    </div>
  )
}
